import { Link } from "react-router-dom";

export default function SeoIntroSection() {
  return (
    <section className="relative py-20 md:py-28 px-6 md:px-10 bg-background-100">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-16 lg:gap-24">
          <div className="lg:w-1/3">
            <p className="text-xs font-medium text-secondary-600 uppercase tracking-widest mb-6">
              /PandaBuy Sheet System
            </p>
            <div className="space-y-4">
              <div className="aspect-square rounded-2xl overflow-hidden border border-background-200/70">
                <img
                  src="https://readdy.ai/api/search-image?query=Organized%20spreadsheet%20on%20laptop%20screen%20showing%20product%20comparison%20data%20grid%20with%20colorful%20highlighted%20cells%2C%20warm%20coffee%20shop%20interior%20background%2C%20soft%20ambient%20lighting%20from%20pendant%20lamps%2C%20productivity%20and%20smart%20shopping%20concept%2C%20clean%20modern%20workspace%20aesthetic&width=400&height=400&seq=seo-img-a&orientation=squarish"
                  alt="PandaBuy spreadsheet system on laptop"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-background-200/70">
                <img
                  src="https://readdy.ai/api/search-image?query=Flat%20lay%20of%20stylish%20fashion%20items%20including%20watch%20sunglasses%20wallet%20and%20scarf%20arranged%20around%20a%20tablet%20showing%20shopping%20interface%2C%20warm%20terracotta%20and%20sand%20color%20palette%2C%20soft%20overhead%20studio%20lighting%2C%20curated%20lifestyle%20photography%2C%20intentional%20shopping%20and%20discovery%20theme&width=400&height=500&seq=seo-img-b&orientation=portrait"
                  alt="Curated shopping discovery"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          <div className="lg:w-2/3">
            <div className="space-y-6">
              <p className="font-heading text-xl md:text-2xl text-foreground-800 leading-relaxed">
                The <strong className="font-semibold text-foreground-950">pandabuyspreadsheet</strong> is
                more than just a list — it is a structured product comparison system that transforms how
                you discover, evaluate, and purchase trending items across the web. Built for savvy
                shoppers who value both speed and detail, our <strong className="font-semibold text-foreground-950">product spreadsheet</strong> model
                organizes thousands of verified listings into clean, filterable formats.
              </p>

              <div className="aspect-[16/7] rounded-2xl overflow-hidden border border-background-200/70 my-6">
                <img
                  src="https://readdy.ai/api/search-image?query=Split%20screen%20comparison%20showing%20chaotic%20messy%20desk%20with%20scattered%20notes%20versus%20clean%20organized%20digital%20workspace%20with%20structured%20data%2C%20before%20and%20after%20concept%2C%20warm%20neutral%20lighting%2C%20minimalist%20editorial%20style%2C%20organization%20and%20efficiency%20theme&width=800&height=350&seq=seo-img-c&orientation=landscape"
                  alt="Before and after: organized shopping with spreadsheets"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>

              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                Every week, our editorial team scans hundreds of sources to curate the most relevant
                finds into a single <strong className="font-semibold text-foreground-950">shopping spreadsheet</strong> that covers footwear,
                apparel, accessories, fragrances, and lifestyle essentials. Rather than jumping between
                dozens of browser tabs and marketplace apps, users open one{" "}
                <strong className="font-semibold text-foreground-950">deal comparison spreadsheet</strong> and
                immediately see side-by-side pricing, availability, and quality notes — saving hours of
                manual research while never missing a worthy pickup.
              </p>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                The philosophy behind the <strong className="font-semibold text-foreground-950">pandabuyspreadsheet</strong> approach
                is transparency through structure. Each entry includes verified source links, community
                feedback signals, and regional availability flags so you can make informed decisions
                without relying on algorithm-driven recommendations that may hide better alternatives.
                Whether you are hunting for seasonal wardrobe refreshes, gift inspiration, or everyday
                staples, the <strong className="font-semibold text-foreground-950">product list finder</strong> within
                every sheet turns scattered browsing into a focused, enjoyable experience.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-6 mt-12 pt-8 border-t border-background-300/60">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-foreground-800"></span>
                <span className="text-xs text-foreground-500 font-medium">.Updated Weekly</span>
              </div>

              <div className="flex items-center gap-2">
                <div className="flex -space-x-3">
                  <div className="w-8 h-8 rounded-full border-2 border-background-100 overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20stylish%20European%20man%20with%20warm%20smile%20on%20soft%20gray%20gradient%20background%2C%20natural%20window%20lighting%2C%20modern%20editorial%20style%2C%20professional%20yet%20approachable%20expression%2C%20neutral%20fashion%20aesthetic%2C%20clean%20portrait%20photography&width=64&height=64&seq=avatar-a-v2&orientation=squarish"
                      alt="Curator"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="w-8 h-8 rounded-full border-2 border-background-100 overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20stylish%20Asian%20woman%20with%20confident%20expression%20on%20soft%20gray%20gradient%20background%2C%20natural%20window%20lighting%2C%20modern%20editorial%20style%2C%20friendly%20and%20approachable%2C%20neutral%20fashion%20aesthetic%2C%20clean%20portrait%20photography&width=64&height=64&seq=avatar-b-v2&orientation=squarish"
                      alt="Curator"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="w-8 h-8 rounded-full border-2 border-background-100 overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20stylish%20African%20man%20with%20warm%20smile%20on%20soft%20gray%20gradient%20background%2C%20natural%20window%20lighting%2C%20modern%20editorial%20style%2C%20approachable%20and%20genuine%20expression%2C%20neutral%20fashion%20aesthetic%2C%20clean%20portrait%20photography&width=64&height=64&seq=avatar-c-v2&orientation=squarish"
                      alt="Curator"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="w-8 h-8 rounded-full border-2 border-background-100 overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20stylish%20European%20woman%20with%20thoughtful%20expression%20on%20soft%20gray%20gradient%20background%2C%20natural%20window%20lighting%2C%20modern%20editorial%20style%2C%20confident%20and%20composed%2C%20neutral%20fashion%20aesthetic%2C%20clean%20portrait%20photography&width=64&height=64&seq=avatar-d-v2&orientation=squarish"
                      alt="Curator"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
                <span className="text-sm text-foreground-600 font-medium">Curated by 10K+ Shoppers</span>
              </div>

              <Link
                to="/guides/product-comparison-mastery"
                className="ml-auto text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-1"
              >
                Read the full guide
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}